import { useQuery } from '@apollo/react-hooks';
import QuestionMarkCircleIcon from '@heroicons/react/solid/QuestionMarkCircleIcon';
import Link from 'next/link';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Box, Column, Row } from '../../../../components/Flex';
import GraphQLErrorText from '../../../../components/GraphQLErrorText';
import Loading from '../../../../components/Loading';
import PlaceholderElement from '../../../../components/transactions/PlaceholderElement';
import Purchase from '../../../../components/transactions/purchase';
import TransactionsTopMenu from '../../../../components/transactions/top-menu/TransactionsTopMenu';
import { LOCAL_STORAGE } from '../../../../constants';
import useAuthOrRedirect from '../../../../hooks/useAuthOrRedirect';
import { ME, PURCHASES } from '../../../../lib/graphql/queries';
import { TRANSACTION_THEMES } from '../../../../theme';
import { useScrollBlock } from '../../../../utils/UseScrollBlock';
import Loadable from 'react-loadable';
import dayjs, { Dayjs } from 'dayjs';
import { analytics } from '../../../../lib/analytics';

const Tour = Loadable({
  loader: () => import('reactour'),
  loading: () => null,
});

const Container = styled(Row)`
  width: 100%;
  margin-right: auto;
  margin-left: auto;
  @media only screen and (max-width: 800px) {
    flex-direction: column;
    width: 100%;
  }
`;

const Card = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  @media only screen and (max-width: 400px) {
    grid-template-columns: 1fr;
  }
  grid-gap: 24px;
`;

const StickyHeader = styled.div`
  position: sticky;
  background-color: #fff;
  z-index: 10;
  top: 0px;
  padding-top: 20px;
  padding-bottom: 10px;

  @media only screen and (max-width: 800px) {
    top: 25px;
    padding-top: 0px;
  }
`;

const flagToType = {
  active: 0,
  future: 1,
  past: 2,
};

const Purchases = () => {
  const [purchaseFlag, setPurchaseFlag] = useState('active');
  const [activePurchases, setActivePurchases] = useState([]);
  const [futurePurchases, setFuturePurchases] = useState([]);
  const [pastPurchases, setPastPurchases] = useState([]);
  const [type, setType] = useState(0);
  const [menuItems, setMenuItems] = useState([
    { title: 'Active Rentals', isActive: true, index: 0, flag: 'active' },
    { title: 'Future Rentals', isActive: false, index: 1, flag: 'future' },
    { title: 'Past Rentals', isActive: false, index: 2, flag: 'past' },
  ]);
  const [tourStep, setTourStep] = useState(0);
  const [isTourOpen, setTourOpen] = useState(false);
  const { data: me } = useQuery(ME);
  const { data, loading, error } = useQuery(PURCHASES, {
    fetchPolicy: 'no-cache',
    ssr: false,
    variables: {
      flag: purchaseFlag,
    },
  });
  const [blockScroll, allowScroll] = useScrollBlock();

  const closeTour = () => {
    localStorage.setItem(
      LOCAL_STORAGE.HAS_TOURED_TRANSACTIONS_PURCHASE,
      'true',
    );
    setTourOpen(false);
    setTourStep(0);
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });
    allowScroll();
  };

  const getTourSteps = () => {
    let steps = [
      {
        selector: '[data-tour="step-1"]',
        content: (
          <p>You can filter out your purchases through theses buttons</p>
        ),
      },
    ];

    switch (type) {
      case 0:
        if (activePurchases.length > 0) {
          steps.push(
            {
              selector: '[data-tour="step-2"]',
              content: (
                <p>
                  This is your order's id, this is what you want to reference if
                  you we're to open a ticket for support
                </p>
              ),
            },
            {
              selector: '[data-tour="step-3"]',
              content: (
                <p>
                  This provides you with the bot's information of your purchase
                </p>
              ),
            },
            {
              selector: '[data-tour="step-4"]',
              content: (
                <p>This provides you with your purchase's information</p>
              ),
            },
            {
              selector: '[data-tour="step-5"]',
              content: (
                <p>
                  This section has all the authentication tokens you must use to
                  gain access to the bot
                </p>
              ),
            },
            {
              selector: '[data-tour="step-6"]',
              content: (
                <p>
                  This section provides you with the links to follow in order to
                  download your bot's installer according to your OS
                </p>
              ),
            },
            {
              selector: '[data-tour="step-7"]',
              content: (
                <p>You can use theses buttons if you enquire any assistance</p>
              ),
            },
            {
              selector: '[data-tour="step-8"]',
              content: (
                <p>
                  This button allows you to reset your bot's license so you can
                  connect to it again
                </p>
              ),
            },
          );
        }
        break;
      case 1:
        if (futurePurchases.length > 0) {
          steps.push(
            {
              selector: '[data-tour="step-2"]',
              content: (
                <p>
                  This is your order's id, this is what you want to reference if
                  you we're to open a ticket for support
                </p>
              ),
            },
            {
              selector: '[data-tour="step-3"]',
              content: (
                <p>
                  This provides you with the bot's information of your purchase
                </p>
              ),
            },
            {
              selector: '[data-tour="step-4"]',
              content: (
                <p>This provides you with your purchase's information</p>
              ),
            },
            {
              selector: '[data-tour="step-5"]',
              content: (
                <p>
                  This section has all the authentication tokens you must use to
                  gain access to the bot
                </p>
              ),
            },
            {
              selector: '[data-tour="step-7"]',
              content: (
                <p>You can use theses buttons if you enquire any assistance</p>
              ),
            },
          );
        }
        break;
      case 2:
        if (pastPurchases.length > 0) {
          steps.push(
            {
              selector: '[data-tour="step-2"]',
              content: (
                <p>
                  This is your order's id, this is what you want to reference if
                  you we're to open a ticket for support
                </p>
              ),
            },
            {
              selector: '[data-tour="step-3"]',
              content: (
                <p>
                  This provides you with the bot's information of your purchase
                </p>
              ),
            },
            {
              selector: '[data-tour="step-4"]',
              content: (
                <p>This provides you with your purchase's information</p>
              ),
            },
            {
              selector: '[data-tour="step-5"]',
              content: (
                <p>
                  This section has all the authentication tokens you must use to
                  gain access to the bot
                </p>
              ),
            },
            {
              selector: '[data-tour="step-10"]',
              content: (
                <p>
                  You can use this button to view the available listings of this
                  bot
                </p>
              ),
            },
          );
        }
        break;
    }

    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      const element = document.querySelector(step.selector);
      // @ts-ignore
      if (element && element.offsetParent === null) {
        steps = steps.filter((el) => el.selector != step.selector);
      } else {
        // @ts-ignore
        steps[i].position = 'left';
      }
    }

    return steps;
  };

  const getDaysRemaining = (
    endDate: Dayjs,
    rentalDays: number,
    startDate: Dayjs,
    id: number,
  ) => {
    const now = dayjs(new Date());
    let daysRemaining = endDate.diff(now, 'days');
    const hoursRemaining = endDate.diff(now, 'hours');
    const hasStarted = now.diff(startDate, 'hours') >= 0;

    if (daysRemaining > 0 || hoursRemaining > 0) {
      if (daysRemaining === 0) {
        daysRemaining = hoursRemaining / 24;
      }
      const percentageOfRemainingDays = (daysRemaining / rentalDays) * 100;
      // console.log(id, percentageOfRemainingDays, daysRemaining)
      return {
        totalDays: rentalDays,
        daysRemaining,
        percentageOfRemainingDays,
        hasStarted,
      };
    } else {
      return {
        totalDays: 0,
        daysRemaining: 0,
        percentageOfRemainingDays: 0,
        hasStarted: false,
      };
    }
  };

  useEffect(() => {
    if (data && data.myPurchases.length > 0) {
      const tempActivePurchases = [];
      const tempFuturePurchases = [];
      const tempPastPurchases = [];
      data.myPurchases.forEach((s) => {
        const flag = getDaysRemaining(
          dayjs(new Date(s.endDate)),
          s.rentalDays,
          dayjs(new Date(s.startDate)),
          s.id,
        );
        if (flag.percentageOfRemainingDays > 0 && flag.hasStarted) {
          if (s.listing) tempActivePurchases.push(s);
        } else if (flag.percentageOfRemainingDays > 0 && !flag.hasStarted) {
          if (s.listing) tempFuturePurchases.push(s);
        } else if (flag.percentageOfRemainingDays <= 0) {
          if (s.listing) tempPastPurchases.push(s);
        }
      });
      setActivePurchases(tempActivePurchases);
      setFuturePurchases(tempFuturePurchases);
      setPastPurchases(tempPastPurchases);
    }
  }, [data]);

  useEffect(() => {
    if (!loading) {
      const hasTouredTransactionsPurchases = localStorage.getItem(
        LOCAL_STORAGE.HAS_TOURED_TRANSACTIONS_PURCHASE,
      );

      if (!hasTouredTransactionsPurchases) {
        setTourOpen(true);
        blockScroll();
      }
    }
  }, [loading]);

  useEffect(() => {
    analytics.page('Rental Purchases');
  }, []);

  if (!useAuthOrRedirect()) return null;
  if (error) return <GraphQLErrorText error={error} />;

  return (
    <>
      <Container>
        <Column>
          <StickyHeader>
            <p className="lg:mb-5 text-2xl md:text-3xl pt-0 pb-2">
              <span className="font-bold">Purchases</span>
            </p>
            <TransactionsTopMenu
              dataTour={'step-1'}
              key="purchase"
              onClick={(e) => {
                setPurchaseFlag(
                  menuItems.find((item) => item.index === e).flag,
                );
                setMenuItems(
                  menuItems.map((item) => {
                    if (item.index === e) {
                      return { ...item, isActive: true };
                    } else {
                      return { ...item, isActive: false };
                    }
                  }),
                );
              }}
              menuItems={menuItems}
            />
          </StickyHeader>
          <Box height={10} />
          {loading && <Loading />}
          {!loading && (
            <>
              {data.myPurchases.length === 0 && (
                <PlaceholderElement
                  cta={<p>You have no {purchaseFlag} purchases</p>}
                />
              )}
              {data.myPurchases.length > 0 && (
                <>
                  <Card>
                    {data.myPurchases.map((o) => (
                      <Purchase
                        rentalDays={o.rentalDays}
                        expiresAt={dayjs(new Date(o.endDate)).format(
                          'MMM dd, yyyy (HH:mm)',
                        )}
                        createdAt={dayjs(new Date(o.createdAt))}
                        orderReference={o.reference}
                        botName={o.listing ? o.listing.bot.name : 'N/A'}
                        startDate={dayjs(new Date(o.startDate))}
                        endDate={dayjs(new Date(o.endDate))}
                        botImage={o.listing ? o.listing.bot.imageUrl : ''}
                        key={o.id}
                        id={o.id}
                        listingId={o?.listing?.id}
                        botId={o?.listing?.bot.id}
                        type={flagToType[purchaseFlag]}
                        tags={o.listing.bot.tags.split(',')}
                        os={o.listing.bot.operatingSystem.split(',')}
                        macDownloadUrl={o.listing.bot.macDownloadUrl}
                        lastResetRequestAt={o.lastResetRequestAt}
                        discordSupportChannelGuildId={
                          o.discordSupportChannelGuildId
                        }
                        discordSupportChannelId={o.discordSupportChannelId}
                        windowsDownloadUrl={o.listing.bot.winDownloadUrl}
                        guideUrl={o.listing.bot.guideUrl}
                        status={TRANSACTION_THEMES.PURCHASES[o.status]}
                        email={o.listing.email}
                        password={o.listing.password}
                        listingKey={o.listing.key}
                        secondaryKey={o.listing.secondaryKey}
                        buyerEmail={me?.me?.email}
                        listingMode={o.listing.bot.listingMode}
                        authType={o.listing.bot.authType}
                      />
                    ))}
                  </Card>
                </>
              )}
            </>
          )}
        </Column>
      </Container>
      {!loading && (
        <Tour
          steps={getTourSteps()}
          isOpen={isTourOpen}
          onRequestClose={() => closeTour()}
          disableInteraction={true}
          showNumber={false}
          rounded={5}
          startAt={tourStep}
          showNavigation={false}
          showCloseButton={false}
          scrollDuration={25}
          lastStepNextButton={
            <button
              onClick={() => closeTour()}
              className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-500 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Got it !
            </button>
          }
        />
      )}
    </>
  );
};

export async function getStaticProps() {
  return { props: {} };
}

export default Purchases;
